import { useState, type MouseEvent } from 'react';
import { isOvernightSchedulePeriod } from './scheduleUtils.ts';
import './WeeklyScheduleEditor.css';

type WeeklySchedulePeriod = {
  start: string;
  end: string;
};

type WeeklyScheduleDay = {
  day: string;
  label: string;
  closed: boolean;
  periods: WeeklySchedulePeriod[];
};

interface WeeklyScheduleEditorProps {
  days: WeeklyScheduleDay[];
  error?: string;
  dirty?: boolean;
  inputClassName: string;
  errorKey?: string;
  errorFieldClassName?: string;
  errorInputClassName?: string;
  disabled?: boolean;
  compactTimeInputs?: boolean;
  allowOvernight?: boolean;
  closedLabel?: string;
  maxPeriodsPerDay?: number;
  onToggleDayClosed: (dayIndex: number, closed: boolean) => void;
  onAddPeriod: (dayIndex: number) => void;
  onUpdatePeriod: (
    dayIndex: number,
    periodIndex: number,
    field: 'start' | 'end',
    value: string,
  ) => void;
  onRemovePeriod: (dayIndex: number, periodIndex: number) => void;
  onReplicateAll?: (period: WeeklySchedulePeriod) => void;
}

const WeeklyScheduleEditor = ({
  days,
  error,
  dirty = false,
  inputClassName,
  errorKey = 'horarios_semana',
  errorFieldClassName = '',
  errorInputClassName = '',
  disabled = false,
  compactTimeInputs = false,
  allowOvernight = true,
  closedLabel = 'Fechado',
  maxPeriodsPerDay,
  onToggleDayClosed,
  onAddPeriod,
  onUpdatePeriod,
  onRemovePeriod,
  onReplicateAll,
}: WeeklyScheduleEditorProps) => {
  const [replicateStart, setReplicateStart] = useState('');
  const [replicateEnd, setReplicateEnd] = useState('');
  const hasError = Boolean(error);
  const timeInputClassName = `${inputClassName} weekly-schedule-time-input${compactTimeInputs ? ' is-compact' : ''} ${compactTimeInputs ? 'text-sm' : 'text-base'} min-h-[44px] cursor-pointer${hasError && errorInputClassName ? ` ${errorInputClassName}` : ''}`;
  const replicateGridClassName = compactTimeInputs
    ? 'grid w-full min-w-0 max-w-full gap-y-3 sm:grid-cols-[8rem_3.5rem_8rem_minmax(0,1fr)] sm:items-end sm:gap-x-3'
    : 'grid w-full min-w-0 max-w-full gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end';
  const periodGridClassName = compactTimeInputs
    ? 'grid w-full min-w-0 max-w-full gap-y-2 sm:grid-cols-[8rem_3.5rem_8rem_minmax(0,1fr)] sm:items-end sm:gap-x-3'
    : 'grid w-full min-w-0 max-w-full gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto] sm:items-end';
  const periodSeparatorClassName = compactTimeInputs
    ? 'hidden min-h-[44px] min-w-14 items-center justify-center px-2 text-sm font-medium text-slate-500 sm:flex'
    : 'hidden min-h-[44px] items-center text-sm text-slate-400 sm:flex';

  const openTimePicker = (event: MouseEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    if (disabled || typeof input.showPicker !== 'function') return;

    try {
      input.showPicker();
    } catch (_) {
      // Some browsers restrict showPicker even during a user gesture.
    }
  };

  const handleReplicateAll = () => {
    if (!replicateStart || !replicateEnd || !onReplicateAll) return;
    onReplicateAll({ start: replicateStart, end: replicateEnd });
  };

  return (
    <div
      className={`form-prompt-field space-y-3${hasError ? ` is-error ${errorFieldClassName}` : ''}${dirty ? ' is-draft' : ''}`}
      data-error-key={errorKey}
    >
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-slate-700">Horários da semana</span>
          <span className="text-rose-600" aria-label="Campo obrigatório">*</span>
          {dirty && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
              Rascunho
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500">
          {allowOvernight
            ? 'Se o atendimento terminar depois da meia-noite, informe o horário normalmente.'
            : 'O horário de início deve ser anterior ao horário de fim no mesmo dia.'}
        </p>
      </div>

      {error ? <p className="text-xs font-semibold text-rose-600">{error}</p> : null}

      {onReplicateAll ? (
        <div className="weekly-schedule-replicate w-full min-w-0 max-w-full rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
          <div className="mb-3">
            <strong className="text-sm text-slate-900">Replicar horário</strong>
            <p className="text-xs text-slate-500">
              Preencha um período para aplicá-lo em todos os dias da semana.
            </p>
          </div>
          <div className={`weekly-schedule-replicate-row ${replicateGridClassName}`}>
            <label className="w-full min-w-0 max-w-full space-y-1 text-xs font-medium text-slate-600">
              <span className="block">Abertura</span>
              <input
                type="time"
                aria-label="Horário de abertura para replicar"
                className={timeInputClassName}
                value={replicateStart}
                disabled={disabled}
                onClick={openTimePicker}
                onChange={(event) => setReplicateStart(event.target.value)}
              />
            </label>
            {compactTimeInputs ? (
              <span className={periodSeparatorClassName} aria-hidden="true">até</span>
            ) : null}
            <label className="w-full min-w-0 max-w-full space-y-1 text-xs font-medium text-slate-600">
              <span className="block">Fechamento</span>
              <input
                type="time"
                aria-label="Horário de fechamento para replicar"
                className={timeInputClassName}
                value={replicateEnd}
                disabled={disabled}
                onClick={openTimePicker}
                onChange={(event) => setReplicateEnd(event.target.value)}
              />
            </label>
            <button
              type="button"
              disabled={disabled || !replicateStart || !replicateEnd}
              onClick={handleReplicateAll}
              className={`box-border min-h-[44px] w-full min-w-0 max-w-full whitespace-normal break-words rounded-lg border border-teal-600 bg-white px-3 py-2 text-sm font-semibold leading-5 text-teal-700 hover:bg-teal-50 disabled:cursor-not-allowed disabled:opacity-45 ${compactTimeInputs ? 'sm:w-auto sm:justify-self-end sm:whitespace-nowrap' : 'md:w-auto md:whitespace-nowrap'}`}
            >
              Replicar para todos
            </button>
          </div>
        </div>
      ) : null}

      <div className="space-y-3">
        {days.map((day, dayIndex) => (
          <section
            key={day.day}
            className={`weekly-schedule-day w-full min-w-0 max-w-full rounded-xl border p-3 sm:p-5 ${day.closed ? 'is-closed border-slate-200 bg-slate-50/70' : 'border-slate-200 bg-white'}`}
          >
            <div className="weekly-schedule-day-header flex min-h-[44px] items-center justify-between gap-3">
              <strong className="text-sm text-slate-900">{day.label}</strong>
              <label className="weekly-schedule-closed-toggle flex min-h-[44px] cursor-pointer items-center gap-2 px-1 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={day.closed}
                  disabled={disabled}
                  onChange={(event) => onToggleDayClosed(dayIndex, event.target.checked)}
                  className="h-4 w-4 accent-teal-600"
                />
                {closedLabel}
              </label>
            </div>

            {!day.closed ? (
              <div className="weekly-schedule-periods mt-3 min-w-0 max-w-full space-y-3">
                {day.periods.length === 0 ? (
                  <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Adicione pelo menos um período de atendimento.
                  </p>
                ) : null}

                {day.periods.map((period, periodIndex) => {
                  const spansNextDay = allowOvernight && isOvernightSchedulePeriod(period);
                  const nextDay = days[(dayIndex + 1) % days.length];

                  return (
                    <div key={`${day.day}-${periodIndex}`} className="min-w-0 max-w-full">
                      <div className={`weekly-schedule-period-row ${periodGridClassName}`}>
                        <label className="w-full min-w-0 max-w-full space-y-1 text-xs font-medium text-slate-600">
                          <span className="block">Abre</span>
                          <input
                            type="time"
                            aria-label={`Início do atendimento de ${day.label}`}
                            className={timeInputClassName}
                            value={period.start}
                            disabled={disabled}
                            onClick={openTimePicker}
                            onChange={(event) => onUpdatePeriod(dayIndex, periodIndex, 'start', event.target.value)}
                          />
                        </label>
                        <span className={periodSeparatorClassName} aria-hidden="true">até</span>
                        <label className="w-full min-w-0 max-w-full space-y-1 text-xs font-medium text-slate-600">
                          <span className="block">Fecha</span>
                          <input
                            type="time"
                            aria-label={`${spansNextDay ? 'Fim no dia seguinte' : 'Fim'} do atendimento de ${day.label}`}
                            className={timeInputClassName}
                            value={period.end}
                            disabled={disabled}
                            onClick={openTimePicker}
                            onChange={(event) => onUpdatePeriod(dayIndex, periodIndex, 'end', event.target.value)}
                          />
                        </label>
                        <button
                          type="button"
                          disabled={disabled}
                          onClick={() => onRemovePeriod(dayIndex, periodIndex)}
                          className={`weekly-schedule-remove-period box-border min-h-[44px] w-full min-w-0 max-w-full whitespace-normal break-words rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium leading-5 text-slate-700 hover:border-rose-300 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-45 sm:w-auto sm:whitespace-nowrap${compactTimeInputs ? ' sm:justify-self-end' : ''}`}
                        >
                          Remover
                        </button>
                      </div>
                      {spansNextDay ? (
                        <p className="mt-2 text-xs font-medium text-teal-700">
                          {day.label}, das {period.start || '--:--'} até {nextDay.label.toLowerCase()}, às {period.end || '--:--'}.
                        </p>
                      ) : null}
                    </div>
                  );
                })}

                <button
                  type="button"
                  disabled={disabled || (maxPeriodsPerDay !== undefined && day.periods.length >= maxPeriodsPerDay)}
                  onClick={() => onAddPeriod(dayIndex)}
                  className="weekly-schedule-add-period min-h-[44px] rounded-lg pr-3 text-sm font-semibold text-teal-700 hover:text-teal-800"
                >
                  + Adicionar período
                </button>
              </div>
            ) : null}
          </section>
        ))}
      </div>
    </div>
  );
};

export default WeeklyScheduleEditor;
